import React from 'react';
import { WiziQuestionState } from '../../utils/wiziQuizStateManager';

interface Question {
  id: number;
  question_order: number;
  subject_id?: number;
  chapter_name?: string;
}

interface WiziQuizNavigatorProps {
  questions: Question[];
  questionStates: WiziQuestionState[];
  currentQuestionIndex: number;
  onQuestionSelect: (index: number) => void;
  onSubmit: () => void;
  disabled?: boolean;
}

const WiziQuizNavigator: React.FC<WiziQuizNavigatorProps> = ({
  questions,
  questionStates,
  currentQuestionIndex,
  onQuestionSelect,
  onSubmit,
  disabled = false,
}) => {
  const getQuestionStyle = (index: number) => {
    const state = questionStates[index];
    const isCurrent = index === currentQuestionIndex;

    if (isCurrent) {
      return 'btn-primary'; // Current question
    }

    if (!state || !state.visited) {
      return 'btn-outline-secondary'; // Not visited (grey)
    }

    // Check status
    switch (state.status) {
      case 'answered':
        return 'btn-success'; // Answered (green)
      case 'answered_marked_review':
        return 'btn-info'; // Answered & marked (purple/blue)
      case 'marked_review':
        return 'btn-warning'; // Marked for review (yellow/orange)
      case 'skipped':
        return 'btn-secondary'; // Skipped
      default:
        if (state.visited && !state.user_answer) {
          return 'btn-danger'; // Visited but not answered (red/orange)
        }
        return 'btn-outline-secondary';
    }
  };

  const getQuestionIcon = (index: number) => {
    const state = questionStates[index];
    const isCurrent = index === currentQuestionIndex;

    if (isCurrent) return 'fas fa-pen';
    if (!state || !state.visited) return 'far fa-circle';

    switch (state.status) {
      case 'answered':
        return 'fas fa-check';
      case 'answered_marked_review':
        return 'fas fa-check-circle';
      case 'marked_review':
        return 'fas fa-bookmark';
      case 'skipped':
        return 'fas fa-forward';
      default:
        return state.visited ? 'fas fa-eye' : 'far fa-circle';
    }
  };

  const getStatistics = () => {
    const stats = {
      notVisited: 0,
      notAnswered: 0,
      answered: 0,
      markedReview: 0,
      answeredMarked: 0,
    };

    questionStates.forEach((state, index) => {
      if (!state || !state.visited) {
        stats.notVisited++;
      } else if (state.status === 'answered') {
        stats.answered++;
      } else if (state.status === 'marked_review') {
        stats.markedReview++;
      } else if (state.status === 'answered_marked_review') {
        stats.answeredMarked++;
      } else if (state.visited && !state.user_answer) {
        stats.notAnswered++;
      }
    });

    return stats;
  };

  const stats = getStatistics();

  return (
    <div className="h-100 d-flex flex-column bg-white border-start">
      {/* Header with Submit */}
      <div className="p-3 border-bottom bg-light">
        <button
          className="btn btn-danger w-100 fw-bold"
          onClick={onSubmit}
          disabled={disabled}
        >
          <i className="fas fa-check-circle me-2"></i>
          SUBMIT TEST
        </button>
      </div>

      {/* Legend */}
      <div className="p-3 border-bottom">
        <h6 className="mb-3">Question Status:</h6>
        <div className="d-flex flex-wrap gap-2 small">
          <div className="d-flex align-items-center">
            <span className="badge bg-secondary me-2" style={{ width: '30px' }}>{stats.notVisited}</span>
            <span className="text-nowrap">Not Visited</span>
          </div>
          <div className="d-flex align-items-center">
            <span className="badge bg-danger me-2" style={{ width: '30px' }}>{stats.notAnswered}</span>
            <span className="text-nowrap">Not Answered</span>
          </div>
          <div className="d-flex align-items-center">
            <span className="badge bg-success me-2" style={{ width: '30px' }}>{stats.answered}</span>
            <span className="text-nowrap">Answered</span>
          </div>
          <div className="d-flex align-items-center">
            <span className="badge bg-warning me-2" style={{ width: '30px' }}>{stats.markedReview}</span>
            <span className="text-nowrap">Marked for Review</span>
          </div>
          <div className="d-flex align-items-center">
            <span className="badge bg-info me-2" style={{ width: '30px' }}>{stats.answeredMarked}</span>
            <span className="text-nowrap">Answered & Marked</span>
          </div>
        </div>
      </div>

      {/* Question Grid */}
      <div className="flex-grow-1 overflow-auto p-3">
        <div className="row g-2">
          {questions.map((question, index) => (
            <div key={question.id} className="col-2">
              <button
                className={`btn ${getQuestionStyle(index)} w-100 p-2`}
                onClick={() => onQuestionSelect(index)}
                disabled={disabled}
                style={{ minHeight: '50px' }}
                title={`Question ${index + 1}`}
              >
                <i className={`${getQuestionIcon(index)} small d-block mb-1`}></i>
                <div className="small">{index + 1}</div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Footer with Progress */}
      <div className="p-3 border-top bg-light">
        <div className="text-center small">
          <div className="fw-bold mb-1">
            Progress: {stats.answered + stats.answeredMarked}/{questions.length}
          </div>
          <div className="progress" style={{ height: '8px' }}>
            <div
              className="progress-bar bg-success"
              role="progressbar"
              style={{
                width: `${((stats.answered + stats.answeredMarked) / questions.length) * 100}%`,
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WiziQuizNavigator;
